(ns hai.mini-apps.frame
  "Shared card frame for mini-app surfaces: a tinted header with an icon/title
   link and round action buttons, plus a placeholder body and the collapsible
   section used to group a card's rows. Each mini-app (notes, tasks, calendar)
   inherits this card in its own ns."
  (:require
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(def ^:private chevron-icon (icons/mui-icon :outlined :chevron_right))

(o/defstyled card :div
  {:background t/--card-surface
   :border-radius "16px"
   :box-shadow "0 2px 4px rgba(45,62,42,0.06), 0 12px 32px rgba(45,62,42,0.10)"
   :display "flex" :flex-direction "column"
   :position "relative"
   :color t/--card-forest}
  ;; Duck-tape strip overhanging the top edge, matching the clock / flow cards.
  ;; Each mini-app shifts it left/right via its own `::before`. (No `overflow:
  ;; hidden` here so the strip can overhang; the header rounds its own top
  ;; corners instead.)
  [:&::before
   {:content "''"
    :position "absolute"
    :top "-8px" :left "40px"
    :width "52px" :height "16px"
    :background "rgba(120,135,100,0.35)"
    :border-radius "2px"
    :box-shadow "0 1px 3px rgba(0,0,0,0.08)"}]
  [:.app-header
   {:display "flex" :align-items "center" :gap "10px"
    :padding "14px 18px"
    :border-top-left-radius "16px" :border-top-right-radius "16px"}]
  [:.app-icon
   {:display "inline-flex" :align-items "center"
    :color t/--note-kraft}
   ;; The page applies `svg {fill: currentColor}`; these are stroke icons, so
   ;; restore fill: none or their shapes fill solid.
   [:svg {:fill "none"}]]
  [:.app-title
   {:font-size "16px" :font-weight "700" :line-height "1.2"
    :margin "0"
    :letter-spacing "-0.005em"
    :color t/--note-kraft}]
  ;; Icon + title as one link to the app's full page.
  [:.app-title-link
   {:display "inline-flex" :align-items "center" :gap "10px"
    :color "inherit"
    :text-decoration "none"
    :cursor "pointer"}
   [:&:hover
    [:.app-title {:text-decoration "underline" :text-underline-offset "3px"}]]]
  [:.app-actions
   {:margin-left "auto"
    :display "flex" :align-items "center" :gap "6px"}]
  [:.app-action-btn
   {:width "28px" :height "28px"
    :border "none" :border-radius "999px"
    :background "transparent"
    :padding "0"
    :color t/--note-kraft
    :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--note-kraft-10}]
   [:svg {:fill "currentColor"}]]
  [:.app-placeholder
   {:padding "32px 18px"
    :font-size "13px"
    :color t/--link-muted
    :text-align "center"}])

;; ---------------------------------------------------------------------------
;; Collapsible section — groups a card's rows under a chevron/label/count toggle
;; ---------------------------------------------------------------------------

(o/defstyled section :div
  {:border-top "1px solid" :border-top-color t/--card-forest-10}
  [:&:first-child {:border-top "0"}]
  [:&:last-child
   {:overflow "hidden"
    :border-bottom-left-radius "16px"
    :border-bottom-right-radius "16px"}])

(o/defstyled section-toggle :button
  {:display "flex" :align-items "center" :gap "10px"
   :width "100%"
   :padding "14px 18px"
   :border "none" :background "transparent"
   :font-family "inherit"
   :text-align "left"
   :cursor "pointer"
   :transition "background 0.15s ease"}
  [:&:hover {:background t/--note-kraft-10}]
  [:.section-chevron
   {:display "inline-flex" :align-items "center"
    :color t/--note-kraft}
   [:svg {:width "16px" :height "16px" :fill "currentColor"
          :transition "transform 0.15s ease"}]]
  [:&.open [:.section-chevron [:svg {:transform "rotate(90deg)"}]]]
  [:.section-label
   {:font-size "13px" :font-weight "700"
    :letter-spacing "0.04em" :text-transform "uppercase"
    :color t/--note-kraft}]
  [:.section-count
   {:font-size "13px" :font-weight "500"
    :color t/--link-muted}])

(o/defstyled section-empty :div
  {:padding "0 18px 14px"
   :font-size "13px"
   :color t/--link-muted})

(defn section-view
  "A collapsible section: a chevron + uppercase LABEL + count toggle over
   `body`, shown only when `open?`. `on-toggle` flips it; an absent/nil `body`
   shows `empty-label` (default \"Nothing here.\")."
  [{:keys [label item-count open? on-toggle empty-label]} body]
  [section
   [section-toggle
    {:type "button"
     :class (when open? "open")
     :on-click on-toggle}
    [:span.section-chevron [chevron-icon]]
    [:span.section-label label]
    [:span.section-count item-count]]
   (when open?
     (or body [section-empty (or empty-label "Nothing here.")]))])
